import { useLazyQuery, useQuery } from "@apollo/client";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import CompanyIssuersQuery from "components/financials/modern/graphql/CompanyIssuersQuery";
import type {
  ICompanyIssuersQuery,
  ICompanyIssuersQueryVariables,
} from "components/financials/modern/graphql/__generated__/CompanyIssuersQuery.generated";
import RequestCompanyIssuerWithTranslatedDescriptionQuery from "components/issuers/graphql/queries/RequestCompanyIssuerWithTranslatedDescriptionQuery";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import ExpandableParagraph from "components/ui/ExpandableParagraph";
import classNames from "components/ui/classNames";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { ISecurityCountry } from "types/graphqlTypes";

interface CompanyDescriptionProps {
  tickerSymbol: string;
  country: ISecurityCountry;
}

export default function CompanyDescription({ tickerSymbol, country }: CompanyDescriptionProps) {
  const intl = useIntl();
  const rootIssuer = useRootIssuer();
  const [hotSwappedTranslationContent, setHotSwapTranslationContent] = useState("");

  const [requestCompanyIssuerWithTranslatedDescription, { loading: isTranslating }] = useLazyQuery(
    RequestCompanyIssuerWithTranslatedDescriptionQuery,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted(data) {
        if (data.companyIssuers.length === 1) {
          setHotSwapTranslationContent(data.companyIssuers[0].description);
        }
      },
    },
  );

  const { data, loading } = useQuery<ICompanyIssuersQuery, ICompanyIssuersQueryVariables>(CompanyIssuersQuery, {
    variables: {
      input: [
        {
          country,
          tickerSymbol,
          vertical: null,
          exchangeId: null,
        },
      ],
      descriptionLanguage: intl.locale.substring(0, 2),
    },
  });

  if (loading) {
    return <LoadingSkeleton count={1} className="h-36" />;
  }

  if (!data || data.companyIssuers.length === 0) {
    const businessDescription = getBusinessDescription(rootIssuer);
    if (businessDescription) {
      return (
        <div className="flex w-full flex-col space-y-2">
          <BusinessDescriptionParagraph>{businessDescription}</BusinessDescriptionParagraph>
        </div>
      );
    }
    return null;
  }

  const issuer = data.companyIssuers[0];

  const appropriateTranslation = (issuer.descriptionTranslations || []).find(
    ({ language }) => intl.locale === language || intl.locale.substring(0, 2) === language,
  );

  if (!issuer.description) {
    const businessDescription = getBusinessDescription(rootIssuer);
    if (businessDescription) {
      return (
        <div className="flex w-full flex-col space-y-2">
          <BusinessDescriptionParagraph>{businessDescription}</BusinessDescriptionParagraph>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="flex w-full flex-col space-y-2">
      <BusinessDescriptionParagraph>
        {hotSwappedTranslationContent || appropriateTranslation?.textContent || issuer.description}
      </BusinessDescriptionParagraph>

      {!hotSwappedTranslationContent && !appropriateTranslation && (
        <button
          type="button"
          disabled={isTranslating}
          className={classNames("font-medium hover:underline", "disabled:pointer-events-none disabled:text-slate-300")}
          onClick={() => {
            requestCompanyIssuerWithTranslatedDescription({
              variables: {
                input: [{ tickerSymbol: issuer.tickerSymbol, vertical: issuer.vertical }],
                language: intl.locale.substring(0, 2),
              },
            });
          }}
        >
          {isTranslating ? (
            <FormattedMessage defaultMessage="Traduzindo..." id="puazta" />
          ) : (
            <FormattedMessage defaultMessage="Ver tradução" id="PM2OcR" />
          )}
        </button>
      )}
    </div>
  );
}

function getBusinessDescription(rootIssuer: ReturnType<typeof useRootIssuer>) {
  if (rootIssuer.__typename === "BrazilianCompanyIssuer") {
    return rootIssuer.businessDescription;
  }
  if (rootIssuer.__typename === "AmericanCompanyIssuer") {
    return rootIssuer.businessDescription;
  }
  if (rootIssuer.__typename === "BrazilianRealEstateFund") {
    return rootIssuer.shortDescription;
  }
  return null;
}

const BusinessDescriptionParagraph = ({ children }: { children: React.ReactNode }) => (
  <>
    <p
      className={classNames(
        "hidden w-full text-sm leading-6 tracking-wide lg:flex",
        "text-slate-600 dark:text-slate-300",
        "text-center sm:text-base sm:leading-7 md:text-left",
      )}
    >
      {children}
    </p>

    <div className="flex lg:hidden">
      <ExpandableParagraph
        className={classNames(
          "w-full text-sm leading-6 tracking-wide",
          "text-slate-600 dark:text-slate-300",
          "text-center sm:text-base sm:leading-7 md:text-left",
        )}
        maxHeightClassName="max-h-48"
      >
        {children}
      </ExpandableParagraph>
    </div>
  </>
);
